import { useEffect, useState } from "react";
import { type SortingState, type Updater } from "@tanstack/react-table";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Plus, Search, X } from "lucide-react";
import { type Dispatch, type UnknownAction } from "redux";

import {
  listFarmerIdentification as listFarmerIdentificationAction,
  listFarmerIdentificationReset as listFarmerIdentificationResetAction,
} from "@/actions";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { SortMenu } from "@/components/SortMenu";
import { WFPButton } from "@/components/ui/button";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
} from "@/components/ui/input-group";
import { ALL_ITEMS_PAGE_SIZE } from "@/constants/pagination";

import { List } from "./components/List";
import { DEFAULT_SORTING, FARMER_IDENTIFICATION_SORT_OPTIONS } from "./components/List/constants";
import { ConnectedFarmerIdentificationEditModal } from "./components/Modal";
import { type FarmerIdentificationModalOptions, type FarmerIdentificationType } from "./types";

type FarmerIdentificationListResponse =
  | FarmerIdentificationType[]
  | {
      count?: number;
      results?: FarmerIdentificationType[];
    };

interface FarmerIdentificationListState {
  data: FarmerIdentificationListResponse;
  errorMessage: string;
  isFetching: boolean;
}

interface RootState {
  listFarmerIdentification: FarmerIdentificationListState;
}

interface StateProps {
  errorMessage: string;
  farmerIdentification: FarmerIdentificationType[];
  isFetching: boolean;
}

interface DispatchProps {
  listFarmerIdentification: () => void;
  listFarmerIdentificationReset: () => void;
}

type WfpManagerFarmerIdentificationViewProps = StateProps & DispatchProps;
type AppDispatch = Dispatch<UnknownAction>;

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

const CLOSED_MODAL_OPTIONS: FarmerIdentificationModalOptions = {
  id: undefined,
  isOpen: false,
  type: undefined,
};

const getListResults = (data: FarmerIdentificationListResponse): FarmerIdentificationType[] => {
  if (Array.isArray(data)) {
    return data;
  }

  return data.results ?? [];
};

export const WfpManagerFarmerIdentificationView = ({
  errorMessage,
  farmerIdentification,
  isFetching,
  listFarmerIdentification,
  listFarmerIdentificationReset,
}: WfpManagerFarmerIdentificationViewProps) => {
  const { t } = useTranslation();
  const [modalOptions, setModalOptions] =
    useState<FarmerIdentificationModalOptions>(CLOSED_MODAL_OPTIONS);
  const [searchTerm, setSearchTerm] = useState("");
  const [sorting, setSorting] = useState<SortingState>(DEFAULT_SORTING);
  const selectedSort = sorting[0];
  const selectedSortColumn =
    FARMER_IDENTIFICATION_SORT_OPTIONS.find(sortOption => sortOption.id === selectedSort?.id)?.id ||
    "";
  const selectedSortDirection = selectedSort?.desc ? "desc" : "asc";

  useEffect(() => {
    listFarmerIdentification();

    return () => {
      listFarmerIdentificationReset();
    };
  }, [listFarmerIdentification, listFarmerIdentificationReset]);

  const onClickAdd = () => {
    setModalOptions({ id: "add", isOpen: true, type: "create" });
  };

  const onSortingChange = (updater: Updater<SortingState>) => {
    setSorting(currentSorting =>
      typeof updater === "function" ? updater(currentSorting) : updater
    );
  };

  const onSortColumnChange = (sortColumn: string) => {
    setSorting([{ id: sortColumn, desc: selectedSortDirection === "desc" }]);
  };

  const onSortDirectionChange = (sortDirection: string) => {
    setSorting([
      {
        id: selectedSortColumn || FARMER_IDENTIFICATION_SORT_OPTIONS[0].id,
        desc: sortDirection === "desc",
      },
    ]);
  };

  return (
    <>
      <main className="flex flex-col gap-6 py-4 px-6 sm:py-6 sm:px-8 md:py-8 md:px-10">
        <div className="flex flex-col gap-4">
          <PageBreadcrumb
            items={[
              { label: t("header.configuration"), href: "/wfpManager/configuration" },
              { label: t("configuration.item.breadcrumb") },
            ]}
          />
          <div className="flex items-center justify-between gap-4">
            <h1 className="font-semibold text-2xl">
              {t("configuration.item.farmerIdentification")}
            </h1>
            <WFPButton type="button" onClick={onClickAdd}>
              <Plus className="size-4" aria-hidden="true" />
              {t("farmerIdentification.newFarmerIdentification")}
            </WFPButton>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <InputGroup className="w-full max-w-xs sm:w-80">
            <InputGroupAddon>
              <Search className="size-4" aria-hidden="true" />
            </InputGroupAddon>
            <InputGroupInput
              id="farmer-identification-search-input"
              type="search"
              value={searchTerm}
              className="[&::-webkit-search-cancel-button]:hidden [&::-webkit-search-decoration]:hidden"
              placeholder={t("farmerIdentification.list.searchPlaceholder")}
              aria-label={t("farmerIdentification.list.searchPlaceholder")}
              onChange={event => setSearchTerm(event.target.value)}
            />
            {searchTerm && (
              <InputGroupAddon align="inline-end">
                <InputGroupButton
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  aria-label={t("commodities.clearFilter")}
                  onClick={() => setSearchTerm("")}
                >
                  <X className="size-3.5" aria-hidden="true" />
                </InputGroupButton>
              </InputGroupAddon>
            )}
          </InputGroup>
          <SortMenu
            options={FARMER_IDENTIFICATION_SORT_OPTIONS}
            sortColumn={selectedSortColumn}
            sortDirection={selectedSortDirection}
            onSortColumnChange={onSortColumnChange}
            onSortDirectionChange={onSortDirectionChange}
          />
        </div>
        <List
          errorMessage={errorMessage}
          farmerIdentification={farmerIdentification}
          isFetching={isFetching}
          onSortingChange={onSortingChange}
          searchTerm={searchTerm}
          setModalOptions={setModalOptions}
          sorting={sorting}
        />
      </main>
      <ConnectedFarmerIdentificationEditModal
        modalOptions={modalOptions}
        setModalOptions={setModalOptions}
      />
    </>
  );
};

export const mapStateToProps = (state: RootState): StateProps => {
  return {
    errorMessage: state.listFarmerIdentification.errorMessage,
    farmerIdentification: getListResults(state.listFarmerIdentification.data),
    isFetching: state.listFarmerIdentification.isFetching,
  };
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    listFarmerIdentification: () =>
      dispatchAction(dispatch, listFarmerIdentificationAction({ page_size: ALL_ITEMS_PAGE_SIZE })),
    listFarmerIdentificationReset: () =>
      dispatchAction(dispatch, listFarmerIdentificationResetAction()),
  };
};

export const ConnectedWfpManagerFarmerIdentificationView = connect(
  mapStateToProps,
  mapDispatchToProps
)(WfpManagerFarmerIdentificationView);
